import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UnsavedChangesWarningOptions {
  message?: string;
}

/**
 * Hook to warn users about unsaved changes when navigating away.
 * Works without data router by intercepting link clicks and browser events.
 *
 * @param isDirty - Whether there are unsaved changes
 * @param options - Configuration options
 * @returns Object with modal state and methods
 */
export function useUnsavedChangesWarning(
  isDirty: boolean,
  options: UnsavedChangesWarningOptions = {}
) {
  const { message = 'You have unsaved changes. Are you sure you want to leave?' } = options;
  const navigate = useNavigate();
  const location = useLocation();

  // State for custom modal
  const [showModal, setShowModal] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);

  // Handle browser refresh/close with beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, message]);

  // Intercept clicks on internal links
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirty) return;

      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        const url = new URL(link.href);
        // Only intercept internal navigation (same origin)
        if (url.origin === window.location.origin && url.pathname !== location.pathname) {
          e.preventDefault();
          e.stopPropagation();
          pendingNavigationRef.current = url.pathname;
          setShowModal(true);
        }
      }
    };

    // Use capture phase to intercept before React Router
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [isDirty, location.pathname]);

  // Proceed with navigation
  const proceed = useCallback(() => {
    setShowModal(false);
    if (pendingNavigationRef.current) {
      const path = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      navigate(path);
    }
  }, [navigate]);

  // Cancel navigation (stay on page)
  const cancel = useCallback(() => {
    setShowModal(false);
    pendingNavigationRef.current = null;
  }, []);

  // Method to attempt navigation with confirmation
  const confirmNavigation = useCallback((path: string) => {
    if (isDirty) {
      pendingNavigationRef.current = path;
      setShowModal(true);
      return false;
    }
    navigate(path);
    return true;
  }, [isDirty, navigate]);

  return {
    showModal,
    proceed,
    cancel,
    message,
    confirmNavigation,
    isDirty,
  };
}
